import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { List, X, ArrowUpRight } from "@phosphor-icons/react";
import { scrollToHash } from "../../lib/api";

const SECTION_LINKS = [
    { label: "Why GoMedVoyage", hash: "#why-gomedvoyage", testid: "nav-why-gomedvoyage" },
    { label: "How It Works", hash: "#how-it-works", testid: "nav-how-it-works" },
    { label: "Specialties", hash: "#specialties", testid: "nav-specialties" },
    { label: "For Hospitals", hash: "#for-hospitals", testid: "nav-for-hospitals" },
];

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [location.pathname]);

    const goSection = (hash) => {
        setOpen(false);
        if (location.pathname !== "/") {
            navigate("/" + hash);
        } else {
            scrollToHash(hash);
        }
    };

    return (
        <header
            data-testid="site-header"
            className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 border-b ${
                scrolled || open
                    ? "bg-white/85 backdrop-blur-xl border-slate-200 shadow-sm"
                    : "bg-white/60 backdrop-blur-md border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="flex h-20 items-center justify-between">
                    <Link to="/" data-testid="header-logo-link" className="flex items-center shrink-0" aria-label="GoMedVoyage home">
                        <img src="/images/logo.png" alt="GoMedVoyage" className="h-11 w-auto" />
                    </Link>

                    <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
                        {SECTION_LINKS.map((l) => (
                            <button
                                key={l.hash}
                                data-testid={l.testid}
                                onClick={() => goSection(l.hash)}
                                className="text-sm font-medium text-slate-600 hover:text-brand-blue transition-colors duration-200"
                            >
                                {l.label}
                            </button>
                        ))}
                        <Link data-testid="nav-about" to="/about" className="text-sm font-medium text-slate-600 hover:text-brand-blue transition-colors duration-200">
                            About
                        </Link>
                        <Link data-testid="nav-contact" to="/contact" className="text-sm font-medium text-slate-600 hover:text-brand-blue transition-colors duration-200">
                            Contact
                        </Link>
                    </nav>

                    <div className="flex items-center gap-3">
                        <Link
                            data-testid="partner-with-us-cta"
                            to="/partner"
                            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-brand-blue px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-blue/20 hover:bg-brand-blue-light hover:shadow-xl active:scale-95 transition-[background-color,box-shadow,transform] duration-200"
                        >
                            Partner With Us
                            <ArrowUpRight size={16} weight="bold" />
                        </Link>
                        <button
                            data-testid="mobile-menu-toggle"
                            onClick={() => setOpen((v) => !v)}
                            className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-slate-200 text-brand-blue bg-white"
                            aria-label="Toggle menu"
                            aria-expanded={open}
                        >
                            {open ? <X size={20} /> : <List size={20} />}
                        </button>
                    </div>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        data-testid="mobile-menu"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-t border-slate-100"
                        aria-label="Mobile"
                    >
                        <div className="px-6 py-6 flex flex-col gap-1">
                            {SECTION_LINKS.map((l) => (
                                <button
                                    key={l.hash}
                                    data-testid={`mobile-${l.testid}`}
                                    onClick={() => goSection(l.hash)}
                                    className="text-left py-3 text-base font-medium text-slate-700 hover:text-brand-blue border-b border-slate-100"
                                >
                                    {l.label}
                                </button>
                            ))}
                            <Link data-testid="mobile-nav-about" to="/about" className="py-3 text-base font-medium text-slate-700 hover:text-brand-blue border-b border-slate-100">
                                About
                            </Link>
                            <Link data-testid="mobile-nav-contact" to="/contact" className="py-3 text-base font-medium text-slate-700 hover:text-brand-blue border-b border-slate-100">
                                Contact
                            </Link>
                            <Link
                                data-testid="mobile-partner-cta"
                                to="/partner"
                                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-brand-blue px-6 py-3.5 text-sm font-semibold text-white"
                            >
                                Partner With Us
                                <ArrowUpRight size={16} weight="bold" />
                            </Link>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
}
